/**
 * Which Settings sections a profile may see.
 *
 * Extracted from Settings.tsx as a pure function for one reason: the four
 * visibility cases are a correctness requirement, and the project's Vitest
 * harness runs in `environment: "node"` with no DOM, so `<Settings/>` cannot be
 * rendered in a test. Deciding visibility here makes each case directly
 * provable, and Settings.tsx then has nothing left to get wrong except which
 * flag guards which block - which a source-level guard checks.
 *
 * The three capabilities are INDEPENDENT. Curation is not a rank above admin and
 * not a subset of it: `can_curate_signals` defaults false for every user
 * including super_admin, and role rank never confers it (see authz.ts, where
 * canCurateSignals deliberately does not fall through to isAdmin). So a curator
 * who is not an admin sees curation and no admin tooling, and an admin who is
 * not a curator sees admin tooling and no curation.
 */

import type { UserProfile } from '@/auth/types';
import { isCuratorCapable } from './capability';

export interface SettingsSectionVisibility {
  /** The "Signal curation" landmark: the curator queue. */
  curation: boolean;
  /** The existing "Admin" landmark: sweep control, candidate review, links. */
  adminTools: boolean;
  /** The existing report downloads. */
  reports: boolean;
  /**
   * Whether the pre-existing Admin/Reports wrapper renders at all. Kept exactly
   * as it was (`isAdmin || canDownloadReport`) so this slice cannot change what
   * a non-curator sees: curation is a SEPARATE landmark, not a new member of
   * that condition.
   */
  adminOrReportsSection: boolean;
}

export function settingsSectionVisibility(
  profile: UserProfile,
): SettingsSectionVisibility {
  const adminTools = profile.role === 'admin' || profile.role === 'super_admin';
  // Firm-wide report access is server-derived independently of role; unchanged
  // from the pre-existing rule.
  const reports = profile.role === 'super_admin' || profile.report_access === true;
  const curation = isCuratorCapable(profile);
  return {
    curation,
    adminTools,
    reports,
    adminOrReportsSection: adminTools || reports,
  };
}
